import React, { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
var TEMPORARY_DATA_ID_PREFIX = 'THRIVE_TEMPORARY_DATA';
export var TEMPORARY_DATA_TTL_DAYS = 7;
/**
 * Disabling this rule because it flags 24, 60, 60, & 1000 as magic numbers
 * which, when multiplied out, represent the length of a day in ms
 */
// eslint-disable-next-line @typescript-eslint/no-magic-numbers
var ONE_DAY = 24 * 60 * 60 * 1000;
/**
 * To mitigate the risk of performance issues when interacting with a Form
 * wrapped in TemporaryBrowserData, we only want to check for expired data
 * every 30 minutes.  Without this timeout, we iterate over every local storage
 * key and check if it's expired on every input change (i.e. on every keystroke
 * of a TextInput)
 *
 * Also disabling this rule for same reason as above
 */
// eslint-disable-next-line @typescript-eslint/no-magic-numbers
var DELETE_ALL_EXPIRED_TIMEOUT_MS = 30 * 60 * 1000;
var getTemporaryData = function (temporaryDataId) {
    var data = localStorage.getItem(temporaryDataId);
    if (data) {
        try {
            return JSON.parse(data);
        }
        catch (error) {
            // JSON.parse throws if the parameter is not a JSON-parsable string.
            // Since this value is coming from localStorage, which can be edited by
            // the user, we handle parsing defensively.
            localStorage.removeItem(temporaryDataId);
            return null;
        }
    }
    return null;
};
var makeTemporaryDataId = function (dataPath) {
    var fullPath = Object.entries(dataPath)
        .map(function (_a) {
        var path = _a[0], id = _a[1];
        return (id ? "".concat(path, "/").concat(id) : null);
    })
        .filter(Boolean)
        .join('/');
    return "".concat(TEMPORARY_DATA_ID_PREFIX, ":").concat(fullPath);
};
var TemporaryBrowserDataContext = createContext({
    deleteData: null,
    data: null,
    dataLastManuallyCleared: null,
    getLatestData: null,
    updateData: null
});
var useTemporaryBrowserData = function () {
    var temporaryBrowserDataContext = useContext(TemporaryBrowserDataContext);
    return temporaryBrowserDataContext !== null && temporaryBrowserDataContext !== void 0 ? temporaryBrowserDataContext : {};
};
var getTemporaryDataForPath = function (dataPath) { return getTemporaryData(makeTemporaryDataId(dataPath)); };
export var TemporaryBrowserData = function (_a) {
    var children = _a.children, dataPath = _a.dataPath, temporaryDataLifespan = _a.temporaryDataLifespan;
    var temporaryDataId = makeTemporaryDataId(dataPath);
    var _b = useState(null), dataLastManuallyCleared = _b[0], setDataLastManuallyCleared = _b[1];
    var _c = useState(Date.now()), expiredDataLastCleared = _c[0], setExpiredDataLastCleared = _c[1];
    var temporaryDataLifespanInMS = (temporaryDataLifespan || TEMPORARY_DATA_TTL_DAYS) * ONE_DAY;
    // Data is stored as an object with the below shape:
    // {
    //   content: string OR object,
    //   lastUpdatedTime: <timestamp>
    // }
    var _d = useState(getTemporaryData(temporaryDataId)), data = _d[0], setData = _d[1];
    var deleteData = function () {
        localStorage.removeItem(temporaryDataId);
        setData(null);
        setDataLastManuallyCleared(Date.now());
    };
    var deleteAllExpiredData = function () {
        var allDataIds = Object.keys(localStorage).filter(function (key) {
            return key.startsWith(TEMPORARY_DATA_ID_PREFIX);
        });
        allDataIds.forEach(function (currentDataId) {
            var _a;
            var lastUpdatedTime = (_a = getTemporaryData(currentDataId)) === null || _a === void 0 ? void 0 : _a.lastUpdatedTime;
            if (lastUpdatedTime) {
                var isExpired = Date.now() > lastUpdatedTime + temporaryDataLifespanInMS;
                if (isExpired) {
                    localStorage.removeItem(currentDataId);
                    // If the ID associated with the current context is expired, set data to null
                    if (temporaryDataId === currentDataId) {
                        setData(null);
                    }
                }
            }
        });
    };
    useEffect(function () {
        if (expiredDataLastCleared + DELETE_ALL_EXPIRED_TIMEOUT_MS <= Date.now()) {
            setExpiredDataLastCleared(Date.now());
            deleteAllExpiredData();
        }
        /**
         * Sometimes users will have data that has outlived its 7-day lifespan.
         * This ensures all expired drafts will be deleted in the following situations:
         *     - on mount
         *     - whenever data changes (w/ 30 minute timeout between checks)
         *     - whenever temporaryDataId changes
         */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, temporaryDataId]);
    var updateData = function (value) {
        if (expiredDataLastCleared + DELETE_ALL_EXPIRED_TIMEOUT_MS <= Date.now()) {
            setExpiredDataLastCleared(Date.now());
            deleteAllExpiredData();
        }
        if (value) {
            var newData = {
                content: value,
                lastUpdatedTime: Date.now()
            };
            setData(newData);
            localStorage.setItem(temporaryDataId, JSON.stringify(newData));
        }
        else {
            deleteData();
        }
    };
    return (React.createElement(TemporaryBrowserDataContext.Provider, { value: {
            deleteData: deleteData,
            data: data,
            dataLastManuallyCleared: dataLastManuallyCleared,
            getLatestData: function () { return getTemporaryData(temporaryDataId); },
            updateData: updateData
        } }, children));
};
TemporaryBrowserData.defaultProps = {
    temporaryDataLifespan: null
};
TemporaryBrowserData.propTypes = {
    children: PropTypes.node.isRequired,
    /**
     * Disabling this rule because there's no generic "key-value" pair
     * prop type and we want to be able to provide any object that matches
     * the following structure to this prop:
     *
     * {
     *    someKey: 'value',
     *    anotherKey: 1,
     * }
     */
    // eslint-disable-next-line react/forbid-prop-types
    dataPath: PropTypes.object.isRequired,
    temporaryDataLifespan: PropTypes.number
};
export default TemporaryBrowserData;
export { useTemporaryBrowserData, getTemporaryDataForPath };
//# sourceMappingURL=TemporaryBrowserData.js.map